import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, X } from "lucide-react";
import { api } from "@/lib/api";
import { formatWhen } from "@/lib/format";
import type { SearchHit } from "@/lib/types";
import { useContactSearch } from "@/components/CommandPalette";
import { useToast } from "@/components/Toaster";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label } from "@/components/ui/input";
import { Select } from "@/components/ui/select";

interface SessionLite {
  id: number;
  name: string | null;
  starts_at: string;
  status: string;
}

/** Register a person (and their guests) for one of the event's dates (events_register). */
export function RegisterDialog({ eventId, sessions, defaultSession, onClose }: { eventId: number; sessions: SessionLite[]; defaultSession?: number; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const open = sessions.filter((s) => s.status === "scheduled");
  const [q, setQ] = useState("");
  const [who, setWho] = useState<SearchHit | null>(null);
  const [session, setSession] = useState<number | "">(defaultSession ?? open[0]?.id ?? "");
  const [waitlist, setWaitlist] = useState(false);
  const [guests, setGuests] = useState<{ first_name: string; last_name: string; email: string }[]>([]);
  const [notes, setNotes] = useState("");
  const search = useContactSearch(q);
  const hits = q.trim() ? (search.data?.hits ?? []).filter((h) => !h.archived).slice(0, 8) : [];
  const register = useMutation({
    mutationFn: () =>
      api<{ status: string }>(`/api/v1/events/${eventId}/registrations`, {
        json: {
          contact_id: who!.contact_id,
          session_id: session || undefined,
          status: waitlist ? "waitlisted" : undefined,
          guests: guests.filter((g) => g.first_name || g.last_name || g.email).map((g) => ({ first_name: g.first_name || undefined, last_name: g.last_name || undefined, email: g.email || undefined })),
          notes: notes || undefined,
          source: "admin",
        },
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["roster", eventId] });
      void qc.invalidateQueries({ queryKey: ["event", eventId] });
      void qc.invalidateQueries({ queryKey: ["upcoming-events", who!.contact_id] });
      toast(`${who!.name} is ${r.status === "waitlisted" ? "on the waitlist" : "registered"}`);
      onClose();
    },
    onError: (e) => toast(e.message, "error"),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(600px,calc(100vw-2rem))]">
        <DialogTitle>Register someone</DialogTitle>
        <DialogDescription>Registering the same person again returns their existing registration. A full date puts them on the waitlist automatically.</DialogDescription>
        <div className="space-y-3 text-sm">
          {who ? (
            <div className="flex items-center justify-between rounded-md border px-3 py-2">
              <span>
                <span className="font-medium">{who.name}</span> <span className="text-xs text-[var(--color-muted-foreground)]">{who.email ?? ""} #{who.contact_id}</span>
              </span>
              <button className="text-xs text-[var(--color-primary)] hover:underline" onClick={() => setWho(null)}>
                Change
              </button>
            </div>
          ) : (
            <div className="space-y-1">
              <Label htmlFor="rg-who">Person</Label>
              <Input id="rg-who" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, email, phone or ID" />
              {hits.length > 0 && (
                <ul className="max-h-48 overflow-y-auto rounded-md border">
                  {hits.map((h) => (
                    <li key={h.contact_id}>
                      <button className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-[var(--color-muted)]" onClick={() => setWho(h)}>
                        <span className="truncate">{h.name || "(no name)"}</span>
                        <span className="truncate text-xs text-[var(--color-muted-foreground)]">{h.email ?? h.phone ?? ""} #{h.contact_id}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-xs text-[var(--color-muted-foreground)]">Not in the CRM yet? Add them as a contact first.</p>
            </div>
          )}
          <div className="grid grid-cols-[1fr_auto] items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="rg-session">Date</Label>
              <Select id="rg-session" value={session} onChange={(e) => setSession(e.target.value ? Number(e.target.value) : "")}>
                {open.length === 0 && <option value="">No open dates</option>}
                {open.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name ? `${s.name}: ` : ""}
                    {formatWhen(s.starts_at)}
                  </option>
                ))}
              </Select>
            </div>
            <label className="flex items-center gap-2 pb-2">
              <input type="checkbox" className="accent-[var(--color-primary)]" checked={waitlist} onChange={(e) => setWaitlist(e.target.checked)} /> Waitlist
            </label>
          </div>
          <div className="space-y-1">
            <Label>Guests</Label>
            {guests.map((g, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_1.4fr_auto] gap-2">
                {(["first_name", "last_name", "email"] as const).map((k) => (
                  <Input
                    key={k}
                    className="h-8"
                    value={g[k]}
                    placeholder={k === "first_name" ? "First name" : k === "last_name" ? "Last name" : "Email (optional)"}
                    aria-label={`Guest ${i + 1} ${k.replace("_", " ")}`}
                    onChange={(e) => setGuests(guests.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)))}
                  />
                ))}
                <button aria-label={`Remove guest ${i + 1}`} className="rounded p-1 hover:bg-[var(--color-muted)]" onClick={() => setGuests(guests.filter((_, j) => j !== i))}>
                  <X className="size-4" />
                </button>
              </div>
            ))}
            <button className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline" onClick={() => setGuests([...guests, { first_name: "", last_name: "", email: "" }])}>
              <Plus className="size-3" /> Add a guest
            </button>
          </div>
          <div className="space-y-1">
            <Label htmlFor="rg-notes">Notes (optional)</Label>
            <Input id="rg-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Paid by phone, order 100231" />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!who || register.isPending || (open.length > 0 && !session)} onClick={() => register.mutate()}>
            Register
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
